import {
  Fragment,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { Link } from "react-router";
import { Avatar } from "@/components/common/Avatar";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { Disclosure } from "@/components/design/Disclosure";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { KpiBadge } from "@/components/design/KpiBadge";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { avatarInitials, AVATAR_PLACEHOLDER_STYLE } from "@/lib/avatar";
import { levelFromPercent } from "@/lib/skill-levels";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useScrollAnchor } from "@/lib/use-scroll-anchor";
import { LockClosedIcon, LockOpenIcon } from "./lock-icons";
import { buildRankingAssistantContext } from "./match-result-assistant-context";
import type { AiAssessmentView, TeamResultView } from "./match-result-codec";
import {
  buildCandidateRanking,
  buildPowerFit,
  buildRadiusInsight,
  buildRankingInsights,
  conditionNumericThreshold,
  constraintSummary,
  DEFAULT_RANK_THRESHOLDS,
  isLenientLevelMatch,
  labelForCondition,
  type ConditionLabelLookup,
  type RankCluster,
  type RankSortBy,
  type RankingActualDistribution,
  type RankingCriterionInsight,
  type RequirementLeniencyView,
  type ResultPerson,
} from "./match-result-derive";
import { RadiusInsightMap } from "./RadiusInsightMap";
import { useConditionLabelFormatters, useConditionLabelLookup } from "./result-panel-labels";
import { Donut } from "./result-panel-visuals";

interface CandidatePowerFitDetailProps {
  person: ResultPerson;
  view: TeamResultView;
  elementWeights?: Readonly<Record<string, number>>;
}

// Nachweis der Person formatieren: genesis liefert den Rohwert (Id/Prozent/Zahl), hier wird er per
// __typename mit den vorhandenen Lookups aufgeloest (Ids -> Label, Level-Prozent -> Stufenname).
function formatActualValue(
  condition: unknown,
  actual: string | null,
  labelLookup: ConditionLabelLookup,
  intl: IntlShape,
): string | null {
  if (!actual) return null;
  const typename =
    condition && typeof condition === "object" ? (condition as { __typename?: string }).__typename : "";
  switch (typename) {
    case "GenderCondition":
      return labelLookup.genders.get(actual) ?? actual;
    case "CountryCondition":
    case "CitizenshipCondition":
      return labelLookup.countries.get(actual) ?? actual;
    case "JobDepartmentCondition":
      return labelLookup.jobDepartments.get(actual) ?? actual;
    case "JobLocationCondition":
      return labelLookup.jobLocations.get(actual) ?? actual;
    case "MaritalCondition":
      return labelLookup.maritalStatuses.get(actual) ?? actual;
    case "ExpertiseCondition": {
      const pct = Number(actual);
      if (Number.isFinite(pct) && pct <= 0) {
        return intl.formatMessage({ id: "match.result.condition.presentActual", defaultMessage: "Vorhanden" });
      }
      return Number.isFinite(pct) ? levelFromPercent(pct)?.name ?? actual : actual;
    }
    case "IndustryExperienceCondition": {
      const years = Number(actual);
      return Number.isFinite(years)
        ? intl.formatMessage(
            {
              id: "match.result.ranking.yearsValue",
              defaultMessage: "{years, plural, one {# Jahr} other {# Jahre}}",
            },
            { years },
          )
        : actual;
    }
    default:
      return actual;
  }
}

// REVIEW steht zwischen C und UNQUALIFIED: fehlende Information ist kein Ausschluss,
// aber auch keine Einordnung ins Band (spark #175).
const RANK_CLUSTERS: RankCluster[] = ["A", "B", "C", "REVIEW", "UNQUALIFIED"];
type ThresholdKey = "a" | "b" | "c";
const THRESHOLD_STEP_PCT = 5;
const INSIGHT_FULFILLED_ORDER = ["order-2", "order-3", "order-4"];
const INSIGHT_BOTTLENECK_ORDER = ["order-6", "order-7", "order-8"];

const criteriaCollator = new Intl.Collator("de", { sensitivity: "base" });

// Bewerbung-Ergebnis: geclustertes Bewerber-Ranking. Der Multi-Range-Slider verschiebt live
// die A/B/C-Schwellen; die Bewertung selbst kommt aus genesis (fulfillment je Kriterium).
export function CandidateRankingPanelContent({
  view,
  persons,
  elementWeights,
  requirementLeniency,
  PowerFitDetail,
}: {
  view: TeamResultView;
  persons: ResultPerson[];
  // Team-Power-Wuensche (Wire 0-1000 je CreativityElement) aus dem Design; treiben den Power-Fit.
  elementWeights?: Readonly<Record<string, number>>;
  // Anforderungspruefung aus dem Design-Snapshot des angezeigten Results: markiert
  // Kulanz-Treffer (erfuellt ohne belegte Stufe) mit amber statt gruen.
  requirementLeniency?: RequirementLeniencyView | null;
  PowerFitDetail: ComponentType<CandidatePowerFitDetailProps>;
}) {
  const intl = useIntl();
  // Drei Cluster-Grenzen als Prozent-State (int vermeidet Float-Drift): "A ab", "B ab",
  // "C ab". Unter C -> unterste Gruppe. Mindestabstand 5, Reihenfolge A > B > C erzwungen,
  // damit keine Zone kollabiert und die Griffe sich nicht ueberholen.
  const [aPct, setAPct] = useState(Math.round(DEFAULT_RANK_THRESHOLDS.a * 100));
  const [bPct, setBPct] = useState(Math.round(DEFAULT_RANK_THRESHOLDS.b * 100));
  const [cPct, setCPct] = useState(Math.round((DEFAULT_RANK_THRESHOLDS.c ?? 0) * 100));
  const thresholds = useMemo(
    () => ({ a: aPct / 100, b: bPct / 100, c: cPct / 100 }),
    [aPct, bPct, cPct],
  );
  const [activeThreshold, setActiveThreshold] = useState<ThresholdKey | null>(null);
  // Slider-Drag: Pointer-Position auf der Skala in 5-Prozent-Schritte uebersetzen.
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingThreshold = useRef<ThresholdKey | null>(null);
  function snapPct(clientX: number): number {
    const el = trackRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const raw = 100 - ((clientX - rect.left) / rect.width) * 100;
    const clamped = Math.min(100, Math.max(0, raw));
    return Math.round(clamped / THRESHOLD_STEP_PCT) * THRESHOLD_STEP_PCT;
  }
  function applyThreshold(key: ThresholdKey, pct: number) {
    const snapped = Math.round(pct / THRESHOLD_STEP_PCT) * THRESHOLD_STEP_PCT;
    if (key === "c") setCPct(Math.min(Math.max(snapped, 0), Math.max(0, bPct - THRESHOLD_STEP_PCT)));
    else if (key === "b") setBPct(Math.min(Math.max(snapped, cPct + THRESHOLD_STEP_PCT), aPct - THRESHOLD_STEP_PCT));
    else setAPct(Math.max(Math.min(snapped, 100), Math.min(100, bPct + THRESHOLD_STEP_PCT)));
  }
  function nearestThreshold(pct: number): ThresholdKey {
    return ([
      { key: "c" as const, dist: Math.abs(cPct - pct) },
      { key: "b" as const, dist: Math.abs(bPct - pct) },
      { key: "a" as const, dist: Math.abs(aPct - pct) },
    ].sort((x, y) => x.dist - y.dist)[0]?.key ?? "a");
  }
  function startThresholdDrag(key: ThresholdKey, e: ReactPointerEvent<HTMLElement>) {
    e.preventDefault();
    e.stopPropagation();
    draggingThreshold.current = key;
    setActiveThreshold(key);
    e.currentTarget.setPointerCapture(e.pointerId);
    applyThreshold(key, snapPct(e.clientX));
  }
  function moveThreshold(e: ReactPointerEvent<HTMLElement>) {
    const key = draggingThreshold.current;
    if (!key) return;
    e.preventDefault();
    applyThreshold(key, snapPct(e.clientX));
  }
  function stopThresholdDrag(e: ReactPointerEvent<HTMLElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    draggingThreshold.current = null;
    setActiveThreshold(null);
  }
  // Power-Fit als zweite, ungemischte Achse (spark #151, @adr SPARK-0030-match-result-scoring-atom-derived@1): gewichtetes Mittel der
  // eigenen Element-Scores. Nur sinnvoll, wenn genesis Powers zurueckgibt.
  const powerFitById = useMemo(
    () => buildPowerFit(view.powers, elementWeights),
    [view.powers, elementWeights],
  );
  const hasPowerFit = powerFitById.size > 0;
  const [sortBy, setSortBy] = useState<RankSortBy>("smycles");
  const ranked = useMemo(
    () => buildCandidateRanking(view.factSkill, persons, thresholds, powerFitById, sortBy),
    [view.factSkill, persons, thresholds, powerFitById, sortBy],
  );
  const personById = useMemo(() => new Map(persons.map((p) => [p.id, p])), [persons]);
  // Top 5 nach ganzheitlicher AI-Eignung; nur qualifizierte (Muss-Gate bestanden) mit Score.
  const topSuitableCandidates = useMemo(
    () =>
      ranked
        .filter((c) => c.qualification === "QUALIFIED" && c.suitabilityScore != null)
        .sort((a, b) => (b.suitabilityScore ?? 0) - (a.suitabilityScore ?? 0))
        .slice(0, 5),
    [ranked],
  );

  const labelLookup = useConditionLabelLookup();
  const conditionLabelFormatters = useConditionLabelFormatters(intl);
  const assistantConditionLabel = useMemo(
    () => (condition: unknown) =>
      labelForCondition(condition, labelLookup, conditionLabelFormatters),
    [conditionLabelFormatters, labelLookup],
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  useAssistantContext(
    useMemo(
      () =>
        buildRankingAssistantContext({
          ranked,
          persons,
          factSkill: view.factSkill,
          thresholds,
          sortBy,
          formatCondition: assistantConditionLabel,
          focusedPersonIds: expanded,
        }),
      [assistantConditionLabel, expanded, persons, ranked, sortBy, thresholds, view.factSkill],
    ),
  );

  const [expandedAiReasons, setExpandedAiReasons] = useState<Set<string>>(new Set());
  const [criteriaTab, setCriteriaTab] = useState<Record<string, "fulfilled" | "lenient" | "unknown" | "unfulfilled">>({});
  const [criteriaReq, setCriteriaReq] = useState<Record<string, "all" | "must" | "kann">>({});
  const [collapsedKinds, setCollapsedKinds] = useState<Set<string>>(new Set());
  // Badges wirken als Tab-Switch: pro Kandidat ist immer nur eine Detail-Ansicht offen
  // (Einschaetzung / Kriterien / Power-Fit). expanded steuert auf/zu, activeView welche Ansicht.
  const [activeView, setActiveView] = useState<Record<string, "smycles" | "criteria" | "power">>({});
  const anchorScroll = useScrollAnchor();
  // Vor jedem Umschalten die Kandidaten-Zeile als Anker festhalten: die Inhalte
  // darunter aendern ihre Hoehe massiv, und der Browser haelt nur scrollTop in
  // Pixeln fest, nicht die Position der geklickten Zeile. Ohne Kompensation
  // springt die Ansicht (Scroll-Clamp bzw. fehlgeleitetes Scroll-Anchoring).
  const holdCandidateAnchor = (personId: string) => {
    anchorScroll(document.querySelector<HTMLElement>(`[data-person-anchor="${personId}"]`));
  };
  function openTab(personId: string, tab: "smycles" | "criteria" | "power") {
    holdCandidateAnchor(personId);
    setActiveView((prev) => ({ ...prev, [personId]: tab }));
    setExpanded((prev) => new Set(prev).add(personId));
  }
  function toggle(id: string) {
    holdCandidateAnchor(id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function areClusterMembersOpen(members: Array<{ personId: string }>) {
    return members.length > 0 && members.every((m) => expanded.has(m.personId));
  }
  function toggleClusterMembers(members: Array<{ personId: string }>) {
    setExpanded((prev) => {
      const next = new Set(prev);
      const close = members.length > 0 && members.every((m) => next.has(m.personId));
      for (const member of members) {
        if (close) next.delete(member.personId);
        else next.add(member.personId);
      }
      return next;
    });
  }
  function toggleAiReason(id: string) {
    setExpandedAiReasons((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const formatActual = (condition: unknown, actual: string | null): string | null =>
    formatActualValue(condition, actual, labelLookup, intl);

  const clusterLabel = (c: RankCluster) => {
    if (c === "UNQUALIFIED") {
      return intl.formatMessage({ id: "match.result.ranking.unqualified", defaultMessage: "Kriterien nicht erfüllt" });
    }
    if (c === "REVIEW") {
      return intl.formatMessage({ id: "match.result.ranking.review", defaultMessage: "Zu prüfen" });
    }
    return c;
  };
  const thresholdHandles: Array<{
    key: ThresholdKey;
    cluster: "A" | "B" | "C";
    value: number;
    min: number;
    max: number;
    testid: string;
    aria: string;
  }> = [
    {
      key: "c",
      cluster: "C",
      value: cPct,
      min: 0,
      max: bPct - 5,
      testid: "ranking-threshold-c",
      aria: intl.formatMessage({
        id: "match.result.ranking.thresholdCAria",
        defaultMessage: "Untergrenze für C",
      }),
    },
    {
      key: "b",
      cluster: "B",
      value: bPct,
      min: cPct + 5,
      max: aPct - 5,
      testid: "ranking-threshold-b",
      aria: intl.formatMessage({
        id: "match.result.ranking.thresholdBAria",
        defaultMessage: "Schwelle zwischen B und C",
      }),
    },
    {
      key: "a",
      cluster: "A",
      value: aPct,
      min: bPct + 5,
      max: 100,
      testid: "ranking-threshold-a",
      aria: intl.formatMessage({
        id: "match.result.ranking.thresholdAAria",
        defaultMessage: "Schwelle zwischen A und B",
      }),
    },
  ];

  const insights = buildRankingInsights(view.factSkill, ranked);
  const radiusInsight = buildRadiusInsight(ranked);
  // Klick auf eine Radius-Bubble springt zur Kandidaten-Zeile im Ranking. Bewusst per DOM-Anker
  // statt Refs: die Zeilen leben tief in Cluster-Disclosures, ein Ref-Geflecht waere schwerer.
  const scrollToCandidate = (personId: string) => {
    const row = document.querySelector<HTMLElement>(`[data-person-anchor="${personId}"]`);
    if (!row) return;
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    row.animate(
      [
        { backgroundColor: "color-mix(in srgb, var(--color-accent) 14%, transparent)" },
        { backgroundColor: "transparent" },
      ],
      { duration: 1600, easing: "ease-out" },
    );
  };
  const hardestMust = insights.hardestMust;
  const topBottlenecks = insights.bottlenecks.slice(0, 3);
  const formatPercent = (value: number | null) =>
    value === null
      ? "–"
      : intl.formatNumber(value, { style: "percent", maximumFractionDigits: 0 });
  const formatAnalysisActual = (condition: unknown, value: number | string): string => {
    const typename =
      condition && typeof condition === "object" ? (condition as { __typename?: string }).__typename : "";
    if (typeof value === "number") {
      if (typename === "IndustryExperienceCondition" || typename === "ExperienceCondition") {
        const years = Math.round(value * 10) / 10;
        return intl.formatMessage(
          {
            id: "match.result.ranking.yearsValue",
            defaultMessage: "{years, plural, one {# Jahr} other {# Jahre}}",
          },
          { years },
        );
      }
      if (typename === "ExpertiseCondition") {
        // Fachwissen als Stufe auf der 7er-Skala statt Prozent. value ist der Storage-Prozent,
        // linear invertiert (toStoragePercent = level * 100/7).
        return intl.formatNumber((value * 7) / 100, { maximumFractionDigits: 1 });
      }
      return intl.formatNumber(value, { maximumFractionDigits: 1 });
    }
    return formatActual(condition, value) ?? value;
  };
  const formatDistribution = (distribution: RankingActualDistribution): string | undefined => {
    // Numerische Verteilungen erklaert die Glyphen-Legende der Achse selbst; nur
    // Text-Verteilungen brauchen die detail-Zeile ("haeufigster Wert ...").
    if (distribution.kind === "numeric") return undefined;
    return intl.formatMessage(
      {
        id: "match.result.insights.textDistribution",
        defaultMessage: "häufigster Wert {value} ({count}/{present}) · {distinct} Varianten",
      },
      {
        value: formatAnalysisActual(distribution.condition, distribution.topValue),
        count: distribution.topCount,
        present: distribution.present,
        distinct: distribution.distinctCount,
      },
    );
  };
  // Ohne constraintSummary: die Composition ("exact 1") ist in der Meta-Box Rauschen.
  const insightLabel = (condition: unknown) =>
    labelForCondition(condition, labelLookup, conditionLabelFormatters);
  const clampPercent = (value: number) => Math.max(0, Math.min(100, Math.round(value)));
  // Der Donut zeigt den Anteil, der das K.O.-Kriterium BESTEHT: ein zu 94% gefuellter
  // Ring liest sich als "94% gut", auch wenn daneben "ausgeschlossen" steht.
  const koPassPercent = hardestMust
    ? clampPercent(((hardestMust.total - hardestMust.count) / Math.max(1, hardestMust.total)) * 100)
    : 100;
  // "n nicht bewertbar" haelt die UNKNOWN-Items sichtbar, die aus met/failed und dem
  // Durchschnitt bewusst herausgehalten werden (spark#175) - sonst suggeriert "3/10
  // erfüllt", die restlichen sieben haetten nachweislich nicht geliefert.
  const unknownPart = (item: RankingCriterionInsight) =>
    item.unknownCount > 0
      ? intl.formatMessage(
          { id: "match.result.insights.unknownShort", defaultMessage: "{count} nicht bewertbar" },
          { count: item.unknownCount },
        )
      : null;
  const bottleneckLines = topBottlenecks.map((item, index) => {
    const gap = Math.max(0, 1 - item.averageFulfillment);
    return {
      key: `bottleneck-${index}`,
      label: insightLabel(item.condition),
      sub: [
        intl.formatMessage(
          { id: "match.result.insights.fulfilledShort", defaultMessage: "{met}/{total} erfüllt" },
          { met: item.metCount, total: item.total },
        ),
        unknownPart(item),
      ]
        .filter(Boolean)
        .join(" · "),
      // Bar kodiert die Luecke: groesstes Problem = vollster Balken, passend zur Sortierung.
      value: gap,
      right: intl.formatMessage(
        { id: "match.result.insights.gapShort", defaultMessage: "{value} Lücke" },
        { value: formatPercent(gap) },
      ),
      detail: item.distribution ? formatDistribution(item.distribution) : undefined,
      distribution: item.distribution ?? undefined,
      requiredValue: conditionNumericThreshold(item.condition),
    };
  });
  // Positives Gegenstueck zu den Bottlenecks: welche Kriterien bringt der Pool mit.
  // Zaehlt Erfuellung inkl. AI-Flips und traegt damit auch bei FULL_AI Information,
  // wo die Ist-Wert-Verteilungen mangels harter actual-Werte leer blieben.
  const fulfilledLines = insights.topFulfilled.map((item, index) => {
    const share = item.total > 0 ? item.metCount / item.total : 0;
    const fulfilled = intl.formatMessage(
      { id: "match.result.insights.fulfilledShort", defaultMessage: "{met}/{total} erfüllt" },
      { met: item.metCount, total: item.total },
    );
    const aiPart =
      item.aiMetCount > 0
        ? intl.formatMessage(
            { id: "match.result.insights.aiMetShort", defaultMessage: "davon {count} per KI erkannt" },
            { count: item.aiMetCount },
          )
        : null;
    return {
      key: `fulfilled-${index}`,
      label: insightLabel(item.condition),
      sub: [fulfilled, aiPart, unknownPart(item)].filter(Boolean).join(" · "),
      value: share,
      right: formatPercent(share),
      // Werte-Achse (Median/Ø/SD) bleibt, aber bewusst OHNE requiredValue: das Soll steht
      // in dieser Spalte schon im Label ("mind. 6"), der Soll-Marker waere doppelt. Bei den
      // Bottlenecks traegt der Marker dagegen die Luecken-Aussage.
      detail: item.distribution ? formatDistribution(item.distribution) : undefined,
      distribution: item.distribution ?? undefined,
    };
  });
  const aiLines = insights.ai.criteria.map((item, index) => ({
    key: `ai-${index}`,
    label: insightLabel(item.condition),
    sub: intl.formatMessage(
      { id: "match.result.insights.aiCriterionCandidates", defaultMessage: "{count} Kandidaten" },
      { count: item.candidateCount },
    ),
    value: item.reasonCount,
    right: intl.formatMessage(
      { id: "match.result.insights.reasonsShort", defaultMessage: "{count} Reasons" },
      { count: item.reasonCount },
    ),
  }));
  const insightComparisonRows = Array.from(
    { length: Math.max(bottleneckLines.length, fulfilledLines.length, 1) },
    (_, index) => index,
  );
  // null = nicht bewertbar (spark#175). Bewusst kein "0 %": das waere eine Behauptung
  // ueber den Kandidaten, die die Daten nicht hergeben.
  const formatScorePercent = (value: number | null, maximumFractionDigits = 0) =>
    value == null ? "–" : intl.formatNumber(value, { style: "percent", maximumFractionDigits });
  const scoreInfoTitle = intl.formatMessage({
    id: "match.result.ranking.scoreInfoTitle",
    defaultMessage: "Rechenweg",
  });
  const scoreInfoAria = intl.formatMessage({
    id: "match.result.ranking.scoreInfoAria",
    defaultMessage: "Rechenweg anzeigen",
  });
  const renderScoreInfo = (candidate: (typeof ranked)[number]) => (
    <div className="space-y-2">
      <div>
        <FormattedMessage
          id="match.result.ranking.scoreFormula"
          defaultMessage="Score = Durchschnitt der Kann-Kriterien."
        />
      </div>
      {/* KANN-only nach Kategorie zusammengefasst (erfuellt/gesamt); MUSS ist ein Gate,
          in der Notiz unten abgedeckt und wuerde die Summe nicht treffen (@adr SPARK-0030-match-result-scoring-atom-derived@1 §3). */}
      <div className="space-y-1">
        {(() => {
          const groups: { kind: string; order: number; met: number; total: number }[] = [];
          for (const criterion of candidate.criteria.filter((c) => c.requirement !== "MUST")) {
            const kind = criterionKindLabel(criterion.condition, intl) ?? "";
            let g = groups.find((x) => x.kind === kind);
            if (!g) {
              g = { kind, order: criterionKindOrder(criterion.condition), met: 0, total: 0 };
              groups.push(g);
            }
            g.total += 1;
            if ((criterion.fulfillment ?? 0) >= 1) g.met += 1;
          }
          groups.sort((a, b) => a.order - b.order);
          return groups.map((g) => (
            <div key={g.kind} className="flex items-center justify-between gap-3">
              <span className="min-w-0 truncate">{g.kind}</span>
              <span className="shrink-0 tabular-nums">
                {g.met}/{g.total}
              </span>
            </div>
          ));
        })()}
      </div>
      <div className="pt-2 tabular-nums">
        {candidate.score == null ? (
          <FormattedMessage
            id="match.result.ranking.scoreNotAssessable"
            defaultMessage="Kann-Kriterien: nicht bewertbar"
          />
        ) : (
          <FormattedMessage
            id="match.result.ranking.scoreCriteriaValue"
            defaultMessage="Kann-Kriterien: {criteria}"
            values={{ criteria: formatScorePercent(candidate.score) }}
          />
        )}
      </div>
      {/* Belastbarkeit getrennt vom Score: ein Wert aus 2 von 5 Kriterien ist etwas anderes
          als derselbe Wert aus 5 von 5. Nie in den Score multipliziert (@adr SPARK-0030-match-result-scoring-atom-derived@1). */}
      {candidate.kannDefined > candidate.kannTotal ? (
        <div className="tabular-nums text-ink/55">
          <FormattedMessage
            id="match.result.ranking.scoreCoverage"
            defaultMessage="{assessable} von {defined} Kriterien bewertbar"
            values={{ assessable: candidate.kannTotal, defined: candidate.kannDefined }}
          />
        </div>
      ) : null}
      <div>
        <FormattedMessage
          id="match.result.ranking.scoreMustGate"
          defaultMessage="Muss-Kriterien bleiben zusätzlich ein hartes Gate."
        />
      </div>
    </div>
  );
  const mustInfoTitle = intl.formatMessage({
    id: "match.result.ranking.mustInfoTitle",
    defaultMessage: "Muss-Kriterien",
  });
  const mustInfoAria = intl.formatMessage({
    id: "match.result.ranking.mustInfoAria",
    defaultMessage: "Muss-Kriterien anzeigen",
  });
  const mustMetLabel = intl.formatMessage({
    id: "match.result.ranking.criterionMet",
    defaultMessage: "erfüllt",
  });
  const mustFailedLabel = intl.formatMessage({
    id: "match.result.ranking.criterionFailed",
    defaultMessage: "nicht erfüllt",
  });
  // Nur die MUSS-Kriterien mit Erfuellt-Status; die Kann-Kriterien haben ihren eigenen
  // Rechenweg im Erfuellt-Badge (@adr SPARK-0030-match-result-scoring-atom-derived@1 §3: Muss ist ein Gate, kein Score-Bestandteil).
  const renderMustInfo = (candidate: (typeof ranked)[number]) => {
    const must = candidate.criteria.filter((criterion) => criterion.requirement === "MUST");
    return (
      <div className="space-y-2">
        <div>
          <FormattedMessage
            id="match.result.ranking.mustFormula"
            defaultMessage="Muss-Kriterien sind harte Gates: jedes muss erfüllt sein."
          />
        </div>
        <div className="space-y-1">
          {must.map((criterion, index) => {
            const met = (criterion.fulfillment ?? 0) >= 1;
            return (
              <div key={index} className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate">{insightLabel(criterion.condition)}</span>
                {/* Glyph plus aria-label tragen die Bedeutung, nicht die Farbe: Rot ist laut
                    UI_CONVENTIONS destruktiven Aktionen vorbehalten. Erfuellt = Accent,
                    verfehlt = zurueckgenommene Ink-Stufe, wie beim Kriterien-Marker unten. */}
                <span
                  aria-label={met ? mustMetLabel : mustFailedLabel}
                  className={`shrink-0 tabular-nums ${met ? "text-accent" : "text-ink/30"}`}
                >
                  {met ? "✓" : "✗"}
                </span>
              </div>
            );
          })}
        </div>
        <div className="pt-1 tabular-nums">
          <FormattedMessage
            id="match.result.ranking.mustMetSummary"
            defaultMessage="Erfüllt: {met}/{total}"
            values={{ met: candidate.mustMet, total: candidate.mustTotal }}
          />
        </div>
      </div>
    );
  };
  const recommendationContent =
    insights.recommendation?.type === "must" ? (
      <FormattedMessage
        id="match.result.insights.recommendMust"
        defaultMessage="{criterion} prüfen oder lockern; dieses Muss-Kriterium schließt {failed}/{total} Kandidaten aus."
        values={{
          criterion: insightLabel(insights.recommendation.criterion.condition),
          failed: insights.recommendation.criterion.failedCount,
          total: insights.recommendation.criterion.total,
        }}
      />
    ) : insights.recommendation?.type === "data" ? (
      <FormattedMessage
        id="match.result.insights.recommendData"
        defaultMessage="Profilwerte für {criterion} nachpflegen; {missing}/{total} Nachweise fehlen."
        values={{
          criterion: insightLabel(insights.recommendation.criterion.condition),
          missing: insights.recommendation.criterion.missingActualCount,
          total: insights.recommendation.criterion.expectedActualCount,
        }}
      />
    ) : insights.recommendation?.type === "ai" ? (
      <FormattedMessage
        id="match.result.insights.recommendAi"
        defaultMessage="{count} AI Reasons bei {candidates} Kandidaten manuell prüfen."
        values={{ count: insights.recommendation.reasonCount, candidates: insights.recommendation.candidateCount }}
      />
    ) : insights.recommendation?.type === "bottleneck" ? (
      <FormattedMessage
        id="match.result.insights.recommendBottleneck"
        defaultMessage="{criterion} ist der stärkste Engpass; Ø Erfüllung {average}."
        values={{
          criterion: insightLabel(insights.recommendation.criterion.condition),
          average: formatPercent(insights.recommendation.criterion.averageFulfillment),
        }}
      />
    ) : (
      <FormattedMessage id="match.result.insights.recommendNone" defaultMessage="Keine unmittelbare Anpassung nötig." />
    );

  return (
    <div className="space-y-3">
      {insights.pool.total > 0 && (
        <div className="box bg-surface p-5" data-testid="ranking-insights">
          <BoxHeader title={<FormattedMessage id="match.result.insights.header" defaultMessage="Match-Analyse" />} />
          <div
            className="mt-3 border-l-2 border-accent bg-accent/5 px-4 py-3"
            data-testid="ranking-insight-recommendation"
          >
            <div className="text-[10px] tracking-[0.2em] text-accent/75">
              <FormattedMessage id="match.result.insights.recommendation" defaultMessage="Empfehlung" />
            </div>
            <div className="mt-1 field-value max-w-5xl text-ink">{recommendationContent}</div>
          </div>
          {/* Mit Radius-Kriterium eine Vier-Spalten-Zeile mit Karte rechts: reine fr-Spalten ohne
              Mindestbreiten, damit Ist-Werte/Bottlenecks mitschrumpfen statt umzubrechen. */}
          <div
            className={`mt-5 grid grid-cols-1 gap-y-6 border-t border-ink/5 pt-4 ${
              radiusInsight
                ? "gap-x-5 @3xl:grid-cols-[0.9fr_1fr_1fr_1.1fr]"
                : "gap-x-7 @lg:grid-cols-[minmax(16rem,0.85fr)_minmax(18rem,1fr)_minmax(24rem,1.25fr)]"
            }`}
          >
            <div className="min-w-0 space-y-6">
              <div className="min-w-0" data-testid="ranking-insight-hardest-must">
                <div className="text-[10px] tracking-[0.2em] text-ink/45">
                  <FormattedMessage id="match.result.insights.hardestMust" defaultMessage="K.O.-Kriterium" />
                </div>
                {hardestMust ? (
                  <div className="mt-2 flex items-center gap-4">
                    <Donut
                      percent={koPassPercent}
                      label={intl.formatMessage({
                        id: "match.result.insights.koPassing",
                        defaultMessage: "erfüllen",
                      })}
                    />
                    <div className="min-w-0">
                      <div className="field-value text-ink">{insightLabel(hardestMust.condition)}</div>
                      <div className="mt-0.5 text-[11px] tabular-nums text-ink/55">
                        <FormattedMessage
                          id="match.result.insights.failCount"
                          defaultMessage="{failed}/{total} ausgeschlossen"
                          values={{ failed: hardestMust.count, total: hardestMust.total }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-1 text-[12px] text-ink/55">
                    <FormattedMessage
                      id="match.result.insights.noExclusion"
                      defaultMessage="Kein Muss-Kriterium schließt aus."
                    />
                  </div>
                )}
              </div>
              <div className="min-w-0" data-testid="ranking-insight-ai">
                <div className="text-[10px] tracking-[0.2em] text-ink/45">
                  <FormattedMessage id="match.result.insights.aiShare" defaultMessage="AI-Anteil" />
                </div>
                <div className="mt-1 field-value tabular-nums text-ink">
                  <FormattedMessage
                    id="match.result.insights.aiReasons"
                    defaultMessage="{count} AI Reasons"
                    values={{ count: insights.ai.reasonCount }}
                  />
                </div>
                <InsightTextList
                  items={aiLines}
                  empty={<FormattedMessage id="match.result.insights.noAiReasons" defaultMessage="Keine AI Reasons." />}
                />
              </div>
            </div>
            <div
              className={`min-w-0 ${radiusInsight ? "@3xl:col-span-2" : "@lg:col-span-2"}`}
              data-testid="ranking-insight-comparison"
            >
              <div
                className={`grid grid-cols-1 gap-y-3 @lg:gap-y-3 ${
                  radiusInsight
                    ? "@3xl:grid-cols-2 @3xl:gap-x-5"
                    : "@lg:grid-cols-[minmax(18rem,1fr)_minmax(24rem,1.25fr)] @lg:gap-x-7"
                }`}
              >
                <div className="order-1 flex items-center gap-1.5 text-[10px] tracking-[0.2em] text-ink/45 @lg:order-none" data-testid="ranking-insight-fulfilled">
                  <FormattedMessage id="match.result.insights.topFulfilled" defaultMessage="Top 3 erfüllte Kriterien" />
                  <InfoTooltip
                    ariaLabel={intl.formatMessage({
                      id: "match.result.insights.personDetailsInfoAria",
                      defaultMessage: "Hinweis zu Personen-Details anzeigen",
                    })}
                    title={
                      <FormattedMessage
                        id="match.result.insights.personDetailsInfoTitle"
                        defaultMessage="Personen-Details"
                      />
                    }
                    placement="bottom"
                  >
                    <FormattedMessage
                      id="match.result.insights.personDetailsInfoBody"
                      defaultMessage="Personen-Details werden nicht als Analyse-Verteilung dargestellt. Sie bleiben im Kandidaten-Detail sichtbar."
                    />
                  </InfoTooltip>
                </div>
                <div className="order-5 text-[10px] tracking-[0.2em] text-ink/45 @lg:order-none" data-testid="ranking-insight-bottlenecks">
                  <FormattedMessage id="match.result.insights.bottlenecks" defaultMessage="Top 3 Bottlenecks" />
                </div>
                {insightComparisonRows.map((index) => {
                  const bottleneck = bottleneckLines[index];
                  const fulfilled = fulfilledLines[index];
                  return (
                    <Fragment key={index}>
                      <div
                        className={`min-w-0 ${
                          INSIGHT_FULFILLED_ORDER[index] ?? "order-4"
                        } @lg:order-none`}
                      >
                        {fulfilled ? (
                          <InsightBarRow item={fulfilled} max={1} formatActual={formatAnalysisActual} />
                        ) : index === 0 ? (
                          <div className="text-[12px] text-ink/55">
                            <FormattedMessage
                              id="match.result.insights.noFulfilled"
                              defaultMessage="Kein Kriterium wird erfüllt."
                            />
                          </div>
                        ) : null}
                      </div>
                      <div
                        className={`min-w-0 ${
                          INSIGHT_BOTTLENECK_ORDER[index] ?? "order-8"
                        } @lg:order-none`}
                      >
                        {bottleneck ? (
                          <InsightBarRow item={bottleneck} max={1} formatActual={formatAnalysisActual} axisOnly />
                        ) : index === 0 ? (
                          <div className="text-[12px] text-ink/55">
                            <FormattedMessage
                              id="match.result.insights.noBottleneck"
                              defaultMessage="Keine auffälligen Engpässe."
                            />
                          </div>
                        ) : null}
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            </div>
            {radiusInsight ? (
              <RadiusInsightMap insight={radiusInsight} onSelectPerson={scrollToCandidate} />
            ) : null}
          </div>
        </div>
      )}
      {(view.factSkill?.teamMembers.length ?? 0) > 0 && (
        <section className="box bg-surface p-5" data-testid="suitability-top-five">
          <BoxHeader
            title={
              <FormattedMessage
                id="match.result.suitability.header"
                defaultMessage="Top 5 – beste Eignung"
              />
            }
            right={
              <span className="text-[10px] tracking-[0.2em] text-ink/45">
                <FormattedMessage
                  id="match.result.suitability.basis"
                  defaultMessage="AI-Gesamteinschätzung"
                />
              </span>
            }
          />
          {topSuitableCandidates.length === 0 ? (
            <div className="rounded-lg border border-dashed border-ink/15 bg-ink/[0.02] px-4 py-5 text-[12px] text-ink/55">
              <FormattedMessage
                id="match.result.suitability.empty"
                defaultMessage="Noch keine AI-Eignungsscores vorhanden. Bitte den Match erneut ausführen."
              />
            </div>
          ) : (
            <ol className="grid grid-cols-[repeat(auto-fit,minmax(10rem,12rem))] justify-start gap-3">
              {topSuitableCandidates.map((candidate, index) => {
                const person = personById.get(candidate.personId);
                const roundedScore = Math.round((candidate.suitabilityScore ?? 0) * 100);
                const hasAvatar = Boolean(person?.avatarContent && person?.avatarMime);
                const scoreLabel = intl.formatMessage(
                  {
                    id: "match.result.suitability.scoreLabel",
                    defaultMessage: "AI-Eignung {score} %",
                  },
                  { score: roundedScore },
                );
                return (
                  <li key={candidate.personId} className="min-w-0" data-testid="suitability-candidate">
                    <Link
                      to={`/p/${candidate.personId}`}
                      className="group block overflow-hidden rounded-md bg-surface ring-1 ring-ink/10 outline-none transition hover:ring-accent/40 focus-visible:ring-2 focus-visible:ring-accent/50"
                      title={intl.formatMessage(
                        {
                          id: "match.result.suitability.openProfile",
                          defaultMessage: "{name} – Profil öffnen",
                        },
                        { name: candidate.personName },
                      )}
                    >
                      <div className="relative aspect-square w-full overflow-hidden">
                        {hasAvatar ? (
                          <img
                            src={`data:${person?.avatarMime};base64,${person?.avatarContent}`}
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        ) : (
                          <div
                            className="absolute inset-0 flex items-center justify-center font-serif text-3xl"
                            style={AVATAR_PLACEHOLDER_STYLE}
                          >
                            {avatarInitials(candidate.personName)}
                          </div>
                        )}
                        <span
                          className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-[11px] font-semibold tabular-nums shadow-sm"
                          style={{ color: "var(--color-on-accent)" }}
                          aria-label={intl.formatMessage(
                            {
                              id: "match.result.suitability.rankLabel",
                              defaultMessage: "Rang {rank}",
                            },
                            { rank: index + 1 },
                          )}
                        >
                          {index + 1}
                        </span>
                        <span
                          className="absolute right-2 top-2 rounded-full bg-surface/90 px-2 py-1 text-[12px] font-semibold tabular-nums text-ink shadow-sm backdrop-blur"
                          aria-label={scoreLabel}
                        >
                          {roundedScore} %
                        </span>
                      </div>
                      <div className="min-w-0 bg-surface px-3 py-2.5">
                        <span className="field-value block truncate text-ink transition group-hover:text-accent">
                          {candidate.personName}
                        </span>
                        <span
                          className="mt-0.5 block min-h-[1rem] truncate text-[11px] text-ink/55"
                          title={person?.profession ?? undefined}
                        >
                          {person?.profession ?? " "}
                        </span>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      )}
    <div className="box bg-surface p-5">
      <BoxHeader title={<FormattedMessage id="match.result.rankingHeader" defaultMessage="Ranking" />} />
      <div className="mb-5 mt-3">
        <div className="flex items-center">
          <span className="shrink-0 text-[11px] text-ink/55">
            <FormattedMessage id="match.result.ranking.thresholdsLabel" defaultMessage="Schwellen" />
          </span>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <span className="shrink-0 text-[10px] tabular-nums text-ink/40" aria-hidden="true">100 %</span>
          <div className="relative min-w-0 flex-1 px-6">
            <div
              ref={trackRef}
              className="relative h-16 cursor-pointer touch-none select-none"
              onPointerDown={(e) => {
                const key = nearestThreshold(snapPct(e.clientX));
                startThresholdDrag(key, e);
              }}
              onPointerMove={moveThreshold}
              onPointerUp={stopThresholdDrag}
              onPointerCancel={stopThresholdDrag}
            >
              <div
                className="pointer-events-none absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full"
                style={{ background: "var(--color-accent)" }}
                aria-hidden="true"
              />
              {thresholdHandles.map((h) => {
                const active = activeThreshold === h.key;
                const left = `${100 - h.value}%`;
                return (
                  <div
                    key={h.key}
                    className="absolute top-1/2 flex -translate-x-1/2 -translate-y-6 flex-col items-center gap-1"
                    style={{ left, zIndex: active ? 30 : 20 }}
                  >
                    <button
                      type="button"
                      role="slider"
                      aria-valuemin={h.min}
                      aria-valuemax={h.max}
                      aria-valuenow={h.value}
                      aria-label={h.aria}
                      aria-valuetext={`${h.cluster} ${h.value} %`}
                      data-testid={h.testid}
                      onPointerDown={(e) => startThresholdDrag(h.key, e)}
                      onPointerMove={moveThreshold}
                      onPointerUp={stopThresholdDrag}
                      onPointerCancel={stopThresholdDrag}
                      onKeyDown={(e) => {
                        const step = e.shiftKey ? 10 : THRESHOLD_STEP_PCT;
                        if (e.key === "ArrowLeft" || e.key === "ArrowDown") applyThreshold(h.key, h.value - step);
                        else if (e.key === "ArrowRight" || e.key === "ArrowUp") applyThreshold(h.key, h.value + step);
                        else if (e.key === "Home") applyThreshold(h.key, h.min);
                        else if (e.key === "End") applyThreshold(h.key, h.max);
                        else return;
                        e.preventDefault();
                      }}
                      className={buttonClasses({
                        variant: "primary",
                        size: "handle",
                        className: "cursor-grab font-semibold leading-none active:cursor-grabbing",
                      })}
                      style={{
                        "--btn-fill": "var(--color-accent)",
                        "--btn-on-fill": "var(--color-on-accent)",
                        transform: active ? "scale(1.08)" : "scale(1)",
                        transitionTimingFunction: "cubic-bezier(.18,1.65,.35,1)",
                      } as CSSProperties}
                    >
                      {/* Gegenskalierung: der Kreis waechst beim Greifen, der Buchstabe bleibt optisch gleich gross. */}
                      <span
                        className="inline-block transition"
                        style={{
                          transform: active ? "scale(calc(1 / 1.08))" : "scale(1)",
                          transitionTimingFunction: "cubic-bezier(.18,1.65,.35,1)",
                        }}
                      >
                        {h.cluster}
                      </span>
                    </button>
                    <span className="whitespace-nowrap text-[10px] tabular-nums text-ink/55">
                      {h.value} %
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <span className="shrink-0 text-[10px] tabular-nums text-ink/40" aria-hidden="true">0 %</span>
        </div>
      </div>
      {hasPowerFit ? (
        <div className="mb-3 flex items-center gap-2">
          <span className="field-label text-ink/55">
            <FormattedMessage id="match.result.ranking.sortBy" defaultMessage="Sortieren nach" />
          </span>
          <SegmentedControl<RankSortBy>
            size="md"
            value={sortBy}
            onChange={setSortBy}
            options={[
              {
                value: "smycles",
                label: <FormattedMessage id="match.result.ranking.sortSmycles" defaultMessage="Smycles" />,
              },
              {
                value: "requirements",
                label: <FormattedMessage id="match.result.ranking.sortRequirements" defaultMessage="Anforderungs-Fit" />,
              },
              {
                value: "power",
                label: <FormattedMessage id="match.result.ranking.sortPower" defaultMessage="Power-Fit" />,
              },
            ]}
          />
        </div>
      ) : null}
      <div className="space-y-2">
        {RANK_CLUSTERS.map((cluster) => {
          const members = ranked.filter((r) => r.cluster === cluster);
          const clusterOpen = areClusterMembersOpen(members);
          const headerAction = members.length > 0 ? (
            <Button
              variant="chip"
              size="sm"
              className="shrink-0"
              onClick={() => toggleClusterMembers(members)}
            >
              {clusterOpen ? (
                <FormattedMessage id="match.result.ranking.collapseAll" defaultMessage="Alle einklappen" />
              ) : (
                <FormattedMessage id="match.result.ranking.expandAll" defaultMessage="Alle aufklappen" />
              )}
            </Button>
          ) : null;
          return (
            // Cluster als Disclosure (Haus-Primitive), damit z.B. ein volles A schnell
            // zugeklappt werden kann statt zu B/C zu scrollen.
            <div key={cluster} data-testid={`ranking-cluster-${cluster.toLowerCase()}`}>
              <Disclosure title={clusterLabel(cluster)} count={members.length} headerAction={headerAction} defaultOpen>
              {members.length === 0 ? (
                cluster === "UNQUALIFIED" ? (
                  // Leerer Zustand erklaert die zwei Wege in diese Gruppe.
                  <div className="max-w-2xl py-0.5 text-[12px] text-ink/55">
                    <FormattedMessage
                      id="match.result.ranking.noMustHint"
                      defaultMessage="Hier landen Bewerber, die ein Muss-Kriterium (Schloss) verfehlen oder deren Score unter der C-Schwelle liegt."
                    />
                  </div>
                ) : cluster === "REVIEW" ? (
                  <div className="max-w-2xl py-0.5 text-[12px] text-ink/55">
                    <FormattedMessage
                      id="match.result.ranking.reviewHint"
                      defaultMessage="Hier landen Bewerber, bei denen ein Kriterium nicht bewertet werden konnte, etwa ein Fachwissen ohne eingestufte Stufe. Sie sind nicht durchgefallen, sondern brauchen einen Blick."
                    />
                  </div>
                ) : (
                  <div className="py-0.5 text-[12px] text-ink/30">—</div>
                )
              ) : (
                <ul>
                  {members.map((m) => {
                    const person = personById.get(m.personId);
                    const open = expanded.has(m.personId);
                    // Badges als Tab-Switch: nur die aktive Ansicht wird gezeigt. Default =
                    // Einschaetzung, wenn ein Smycles-Score existiert, sonst Kriterien.
                    const tab = activeView[m.personId] ?? (m.suitabilityScore != null ? "smycles" : "criteria");
                    const activeReq = criteriaReq[m.personId] ?? "all";
                    const smyclesActive = open && tab === "smycles";
                    const mustActive = open && tab === "criteria" && activeReq === "must";
                    const kannActive = open && tab === "criteria" && activeReq === "kann";
                    const powerActive = open && tab === "power";
                    // Zweite Achse: Power-Fit als eigenes, ungemischtes Prozent-Badge neben dem
                    // Anforderungs-Fit (spark #151). Nur wenn Powers vorliegen.
                    const powerFitLabel = m.powerFit != null ? `${Math.round(m.powerFit)} %` : null;
                    // Muss-Erfuellung getrennt vom KANN-Score (spark #151): ein qualifizierter
                    // Kandidat mit schwachem KANN (z.B. "0 %") soll nicht als Versager gelesen
                    // werden. Der Muss-Chip bleibt immer sichtbar, ohne Muss-Kriterien ausgegraut,
                    // damit erkennbar ist, dass es keine gibt (geschlossenes Schloss = Muss).
                    const hasMust = m.mustTotal > 0;
                    const mustPillTitle = hasMust
                      ? intl.formatMessage({ id: "match.result.ranking.mustTitle", defaultMessage: "Muss-Kriterien" })
                      : intl.formatMessage({ id: "match.result.ranking.mustNone", defaultMessage: "Keine Muss-Kriterien" });
                    // KI-Schalter des Matches (MatchData.aiEnabled): der Editor schreibt ihn als
                    // aiMode auf die KI-faehigen Conditions. Aus -> keine AI-Einschaetzungs-Region,
                    // auch keine leere Spalte reservieren.
                    const aiEnabled = m.criteria.some(
                      (rc) => (rc.condition as { aiMode?: string } | null)?.aiMode === "FULL_AI",
                    );
                    return (
                      <li
                        key={m.personId}
                        data-person-anchor={m.personId}
                        className="group/candidate border-t border-ink/5 py-2.5 first:border-t-0"
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          aria-expanded={open}
                          onClick={() => toggle(m.personId)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggle(m.personId);
                            }
                          }}
                          className="-mx-2 -my-2.5 flex cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 outline-none transition-colors hover:bg-accent/5 focus-visible:ring-2 focus-visible:ring-accent/40"
                        >
                          <Link
                            to={`/p/${m.personId}`}
                            onClick={(e) => e.stopPropagation()}
                            title={intl.formatMessage({
                              id: "match.result.ranking.openProfile",
                              defaultMessage: "Profil öffnen",
                            })}
                            className="shrink-0 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                          >
                            <Avatar
                              person={{
                                name: m.personName,
                                avatarContent: person?.avatarContent,
                                avatarMime: person?.avatarMime,
                              }}
                              size="lg"
                            />
                          </Link>
                          <div className="flex w-[15rem] shrink-0 items-center gap-1.5 py-1.5">
                            <span className="field-value min-w-0 truncate text-ink transition group-hover/candidate:text-accent">
                              {m.personName}
                            </span>
                            <span
                              aria-hidden="true"
                              className={`shrink-0 text-[11px] text-ink/40 transition-transform duration-[var(--duration-base)] ${
                                open ? "rotate-90" : ""
                              }`}
                            >
                              ›
                            </span>
                          </div>
                          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                            {m.suitabilityScore != null ? (
                              <KpiBadge
                                active={smyclesActive}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTab(m.personId, "smycles");
                                }}
                              >
                                <FormattedMessage id="match.result.ranking.kpiSmycles" defaultMessage="Smycles Score" />
                                <span>{Math.round(m.suitabilityScore * 100)} %</span>
                              </KpiBadge>
                            ) : null}
                            <KpiBadge
                              tone={hasMust ? "neutral" : "muted"}
                              active={mustActive}
                              title={mustPillTitle}
                              onClick={(e) => {
                                // Tab "Kriterien", Filter auf Muss (geschlossenes Schloss). Auch ohne
                                // Muss-Kriterien klickbar; die Liste bleibt dann leer, was das
                                // ausgegraute Badge bestaetigt.
                                e.stopPropagation();
                                setCriteriaReq((prev) => ({ ...prev, [m.personId]: "must" }));
                                // Auf den Tab schalten, der etwas zu zeigen hat: sobald ein Muss
                                // erfuellt ist, ist "Erfuellt" nicht leer. Nur wenn kein einziges
                                // erfuellt ist, waere dieser Tab leer, dann direkt "Nicht erfuellt".
                                setCriteriaTab((prev) => ({
                                  ...prev,
                                  [m.personId]: m.mustMet > 0 ? "fulfilled" : "unfulfilled",
                                }));
                                openTab(m.personId, "criteria");
                              }}
                              info={
                                hasMust
                                  ? {
                                      title: mustInfoTitle,
                                      ariaLabel: mustInfoAria,
                                      width: "w-[20rem] max-w-[20rem]",
                                      content: renderMustInfo(m),
                                    }
                                  : undefined
                              }
                            >
                              <LockClosedIcon />
                              <FormattedMessage id="match.result.ranking.must" defaultMessage="Muss" />
                              <span>
                                {m.mustMet}/{m.mustTotal}
                              </span>
                            </KpiBadge>
                            <KpiBadge
                              active={kannActive}
                              title={intl.formatMessage({
                                id: "match.result.ranking.kannTitle",
                                defaultMessage: "Kann-Kriterien",
                              })}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCriteriaReq((prev) => ({ ...prev, [m.personId]: "kann" }));
                                setCriteriaTab((prev) => ({ ...prev, [m.personId]: "fulfilled" }));
                                openTab(m.personId, "criteria");
                              }}
                              info={{ title: scoreInfoTitle, ariaLabel: scoreInfoAria, content: renderScoreInfo(m) }}
                            >
                              <LockOpenIcon />
                              <FormattedMessage id="match.result.ranking.optional" defaultMessage="Kann" />
                              {/* Summe der Erfuellungsgrade, nicht die Anzahl voll erfuellter
                                  Kriterien: 2,4/3 ist genau der Score (80 %), nach dem
                                  "Anforderungs-Fit" sortiert und geclustert wird. */}
                              {/* Kein "0/0", wenn zwar Kann-Kriterien definiert, aber keines bewertbar
                                  ist: das läse sich wie "alles verfehlt" (spark#175). */}
                              <span>
                                {m.kannTotal === 0 && m.kannDefined > 0
                                  ? "–"
                                  : `${intl.formatNumber(m.kannSum, { maximumFractionDigits: 1 })}/${m.kannTotal}`}
                              </span>
                            </KpiBadge>
                            {powerFitLabel ? (
                              <KpiBadge
                                active={powerActive}
                                title={intl.formatMessage({
                                  id: "match.result.ranking.powerFitTooltip",
                                  defaultMessage: "Power-Fit: gewichtetes Mittel der eigenen Power-Werte dieser Person.",
                                })}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTab(m.personId, "power");
                                }}
                              >
                                <FormattedMessage id="match.result.ranking.kpiPowerFit" defaultMessage="Power-Fit" />
                                <span>{powerFitLabel}</span>
                              </KpiBadge>
                            ) : null}
                            {/* Im Prüf-Cluster muss ohne Aufklappen erkennbar sein, WORAN es hakt:
                                ein unbewertbares Muss ist etwas anderes als ein nicht bewertbarer
                                Kann-Score (spark#175). Beides kann gleichzeitig zutreffen. */}
                            {m.cluster === "REVIEW" ? (
                              <span
                                className="rounded-full bg-ink/[0.06] px-2 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-[0.12em] text-ink/55 ring-1 ring-ink/10"
                                data-testid={`review-reason-${m.personId}`}
                              >
                                {m.qualification === "REVIEW_REQUIRED" && m.score === null ? (
                                  <FormattedMessage
                                    id="match.result.ranking.reviewReasonBoth"
                                    defaultMessage="Muss und Kann nicht bewertbar"
                                  />
                                ) : m.qualification === "REVIEW_REQUIRED" ? (
                                  <FormattedMessage
                                    id="match.result.ranking.reviewReasonMust"
                                    defaultMessage="Muss-Kriterium nicht bewertbar"
                                  />
                                ) : (
                                  <FormattedMessage
                                    id="match.result.ranking.reviewReasonKann"
                                    defaultMessage="Kann-Kriterien nicht bewertbar"
                                  />
                                )}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div
                          inert={!open}
                          className={`grid transition-[grid-template-rows] duration-[var(--duration-base)] ${
                            open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                          }`}
                        >
                          <div className="overflow-hidden">
                            {/* Side-by-side erst ab @7xl: die Spalten-Minima (7rem Einzug + 26rem
                                Anforderung + 20rem Nachweis + 18rem Einschaetzung + Gap) brauchen
                                ~72rem; ab @4xl lief das Grid rechts aus der Karte und der
                                Einschaetzungstext wurde an der Kante abgeschnitten. */}
                            <div className="pb-2.5 pl-1 pr-1 pt-4">
                              <div className="pl-3">
                              {tab === "criteria" ? (
                              <div className="min-w-0">
                              {m.criteria.length === 0 ? (
                                <div className="text-[11px] text-ink/40">
                                  <FormattedMessage
                                    id="match.result.ranking.noCriteria"
                                    defaultMessage="Keine Kriterien definiert"
                                  />
                                </div>
                              ) : (
                                <div className="max-w-5xl">
                                  {(() => {
                                    const withMeta = m.criteria.map((rc, originalIndex) => {
                                      const met = rc.fulfillment != null && rc.fulfillment >= 1;
                                      const unknown = !rc.assessable;
                                      return {
                                        rc,
                                        originalIndex,
                                        met,
                                        unknown,
                                        lenient: isLenientLevelMatch(
                                          rc.condition, rc.requirement, rc.aiReason, met, requirementLeniency,
                                        ),
                                      };
                                    });
                                    const fulfilledCount = withMeta.filter((o) => o.met).length;
                                    const unknownCount = withMeta.filter((o) => o.unknown).length;
                                    const lenientCount = withMeta.filter((o) => o.lenient).length;
                                    const unfulfilledCount = withMeta.filter((o) => !o.met && !o.unknown).length;
                                    const tab = criteriaTab[m.personId] ?? "fulfilled";
                                    const reqFilter = criteriaReq[m.personId] ?? "all";
                                    const inTab = withMeta.filter((o) =>
                                      tab === "lenient" ? o.lenient : tab === "unknown" ? o.unknown : tab === "fulfilled" ? o.met : !o.met && !o.unknown,
                                    );
                                    const mustCount = inTab.filter((o) => o.rc.requirement === "MUST").length;
                                    const kannCount = inTab.length - mustCount;
                                    const shown = inTab
                                      .filter((o) =>
                                        reqFilter === "all"
                                          ? true
                                          : reqFilter === "must"
                                            ? o.rc.requirement === "MUST"
                                            : o.rc.requirement !== "MUST",
                                      )
                                      .sort((left, right) => {
                                        const ll = labelForCondition(left.rc.condition, labelLookup, conditionLabelFormatters);
                                        const rl = labelForCondition(right.rc.condition, labelLookup, conditionLabelFormatters);
                                        return criteriaCollator.compare(ll, rl) || left.originalIndex - right.originalIndex;
                                      });
                                    const groups: { kind: string; order: number; items: typeof shown }[] = [];
                                    for (const item of shown) {
                                      const kind = criterionKindLabel(item.rc.condition, intl) ?? "";
                                      let g = groups.find((x) => x.kind === kind);
                                      if (!g) {
                                        g = { kind, order: criterionKindOrder(item.rc.condition), items: [] };
                                        groups.push(g);
                                      }
                                      g.items.push(item);
                                    }
                                    groups.sort((a, b) => a.order - b.order);
                                    return (
                                      <>
                                        <div className="flex flex-wrap items-center gap-3 pb-3">
                                          <SegmentedControl<"fulfilled" | "lenient" | "unknown" | "unfulfilled">
                                            size="kpi"
                                            value={tab}
                                            onChange={(v) => {
                                              holdCandidateAnchor(m.personId);
                                              setCriteriaTab((prev) => ({ ...prev, [m.personId]: v }));
                                            }}
                                            options={[
                                              {
                                                value: "fulfilled",
                                                label: (
                                                  <FormattedMessage
                                                    id="match.result.ranking.fulfilledTab"
                                                    defaultMessage="Erfüllt ({count})"
                                                    values={{ count: fulfilledCount }}
                                                  />
                                                ),
                                              },
                                              // Nur sichtbar, wenn es Kulanz-Treffer gibt (bei "Beides streng"
                                              // kann es keine geben, ein leerer Tab waere Rauschen).
                                              ...(lenientCount > 0
                                                ? [
                                                    {
                                                      value: "lenient" as const,
                                                      label: (
                                                        // Bewusst ohne amber Punkt: Chromatik bleibt den Status-Dots
                                                        // in den Zeilen vorbehalten (ive-design-principles: monochrome
                                                        // Controls), die Bruecke traegt das geteilte Wort "Kulant".
                                                        <span
                                                          title={intl.formatMessage({
                                                            id: "match.result.ranking.lenientMatchHint",
                                                            defaultMessage:
                                                              "Erfüllt per kulanter Anforderungsprüfung: Fachwissen vorhanden, geforderte Stufe nicht belegt.",
                                                          })}
                                                        >
                                                          <FormattedMessage
                                                            id="match.result.ranking.lenientTab"
                                                            defaultMessage="Kulant erfüllt ({count})"
                                                            values={{ count: lenientCount }}
                                                          />
                                                        </span>
                                                      ),
                                                    },
                                                  ]
                                                : []),
                                              {
                                                value: "unfulfilled",
                                                label: (
                                                  <FormattedMessage
                                                    id="match.result.ranking.unfulfilledTab"
                                                    defaultMessage="Nicht erfüllt ({count})"
                                                    values={{ count: unfulfilledCount }}
                                                  />
                                                ),
                                              },
                                              // Nur anbieten, wenn es etwas zu prüfen gibt: ein leerer dritter
                                              // Tab wäre bei vollständigen Profilen reines Rauschen.
                                              ...(unknownCount > 0
                                                ? [
                                                    {
                                                      value: "unknown" as const,
                                                      label: (
                                                        <FormattedMessage
                                                          id="match.result.ranking.unknownTab"
                                                          defaultMessage="Zu prüfen ({count})"
                                                          values={{ count: unknownCount }}
                                                        />
                                                      ),
                                                    },
                                                  ]
                                                : []),
                                            ]}
                                          />
                                          <SegmentedControl<"all" | "must" | "kann">
                                            size="kpi"
                                            className={`order-first ${
                                              reqFilter === "must"
                                                ? "[--pill-active-fg:var(--color-on-accent)] [--pill-indicator-bg:var(--color-accent)]"
                                                : ""
                                            }`}
                                            value={reqFilter}
                                            onChange={(v) => {
                                              holdCandidateAnchor(m.personId);
                                              setCriteriaReq((prev) => ({ ...prev, [m.personId]: v }));
                                            }}
                                            options={[
                                              {
                                                value: "all",
                                                label: <FormattedMessage id="match.result.ranking.filterAll" defaultMessage="Alle" />,
                                              },
                                              {
                                                value: "must",
                                                label: (
                                                  <span
                                                    className="inline-flex items-center gap-1 [&>svg]:h-3 [&>svg]:w-3"
                                                    title={intl.formatMessage({ id: "match.result.ranking.filterMust", defaultMessage: "Nur Muss-Kriterien" })}
                                                  >
                                                    <LockClosedIcon />
                                                    <span className="tabular-nums">{mustCount}</span>
                                                  </span>
                                                ),
                                              },
                                              {
                                                value: "kann",
                                                label: (
                                                  <span
                                                    className="inline-flex items-center gap-1 [&>svg]:h-3 [&>svg]:w-3"
                                                    title={intl.formatMessage({ id: "match.result.ranking.filterKann", defaultMessage: "Nur Kann-Kriterien" })}
                                                  >
                                                    <LockOpenIcon />
                                                    <span className="tabular-nums">{kannCount}</span>
                                                  </span>
                                                ),
                                              },
                                            ]}
                                          />
                                        </div>
                                        {groups.length === 0 ? (
                                          // Ive-Loop Schritt 4: Leerzustand erklaert die Ursache (aktive
                                          // Filter-Kombination) und bietet genau einen Ausweg an, statt als
                                          // Fluesterzeile unterzugehen ("wo sind meine Ergebnisse?").
                                          (() => {
                                            const tabNames = {
                                              fulfilled: intl.formatMessage({ id: "match.result.ranking.tabNameFulfilled", defaultMessage: "Erfüllt" }),
                                              lenient: intl.formatMessage({ id: "match.result.ranking.tabNameLenient", defaultMessage: "Kulant erfüllt" }),
                                              unknown: intl.formatMessage({ id: "match.result.ranking.tabNameUnknown", defaultMessage: "Zu prüfen" }),
                                              unfulfilled: intl.formatMessage({ id: "match.result.ranking.tabNameUnfulfilled", defaultMessage: "Nicht erfüllt" }),
                                            } as const;
                                            const reqCulprit = reqFilter !== "all" && inTab.length > 0;
                                            const fallbackTab =
                                              fulfilledCount > 0 ? ("fulfilled" as const)
                                              : lenientCount > 0 ? ("lenient" as const)
                                              : ("unfulfilled" as const);
                                            return (
                                              // Hinweis-Block mit einmaligem hint-flash; key = Ursache, damit der
                                              // Flash nur bei einer NEUEN leeren Filterkombination erneut laeuft.
                                              // Bewusst ohne die border-l-2 des Hinweis-Primitives: die Kandidaten-
                                              // Sektion bringt bereits eine linke Linie mit, zwei Linien nebeneinander
                                              // lesen sich als Layoutfehler. Aufmerksamkeit = hint-flash + bg-accent/5.
                                              <div
                                                key={`${tab}:${reqFilter}`}
                                                className="hint-flash my-2 bg-accent/5 px-4 py-3"
                                                data-testid={`criteria-empty-${m.personId}`}
                                              >
                                                <div className="text-[10px] tracking-[0.2em] text-accent/75">
                                                  <FormattedMessage
                                                    id="match.result.ranking.emptyHintLabel"
                                                    defaultMessage="Hinweis"
                                                  />
                                                </div>
                                                <div className="mt-1 field-value flex flex-wrap items-center gap-x-4 gap-y-1 text-ink">
                                                <span>
                                                  {reqCulprit ? (
                                                    <FormattedMessage
                                                      id="match.result.ranking.emptyFilteredTab"
                                                      defaultMessage="Keine {requirement}-Kriterien unter „{tab}“."
                                                      values={{
                                                        requirement:
                                                          reqFilter === "must"
                                                            ? intl.formatMessage({ id: "match.result.ranking.must", defaultMessage: "Muss" })
                                                            : intl.formatMessage({ id: "match.result.ranking.optional", defaultMessage: "Kann" }),
                                                        tab: tabNames[tab],
                                                      }}
                                                    />
                                                  ) : (
                                                    <FormattedMessage
                                                      id="match.result.ranking.emptyTab"
                                                      defaultMessage="Unter „{tab}“ gibt es keine Kriterien."
                                                      values={{ tab: tabNames[tab] }}
                                                    />
                                                  )}
                                                </span>
                                                {reqCulprit ? (
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                      holdCandidateAnchor(m.personId);
                                                      setCriteriaReq((prev) => ({ ...prev, [m.personId]: "all" }));
                                                    }}
                                                  >
                                                    <FormattedMessage
                                                      id="match.result.ranking.emptyShowAll"
                                                      defaultMessage="Alle Kriterien zeigen"
                                                    />
                                                  </Button>
                                                ) : (
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                      holdCandidateAnchor(m.personId);
                                                      setCriteriaTab((prev) => ({ ...prev, [m.personId]: fallbackTab }));
                                                      setCriteriaReq((prev) => ({ ...prev, [m.personId]: "all" }));
                                                    }}
                                                  >
                                                    <FormattedMessage
                                                      id="match.result.ranking.emptySwitchTab"
                                                      defaultMessage="Zu „{tab}“ wechseln"
                                                      values={{ tab: tabNames[fallbackTab] }}
                                                    />
                                                  </Button>
                                                )}
                                                </div>
                                              </div>
                                            );
                                          })()
                                        ) : (
                                          groups.map((group) => {
                                            const kindKey = `${m.personId}:${group.kind}`;
                                            const kindOpen = !collapsedKinds.has(kindKey);
                                            // Chromium occasionally keeps a stale two-column balance after a
                                            // tall AI reason in this CSS multi-column block is closed. A key
                                            // derived from the open reasons remounts only this criterion group,
                                            // forcing a fresh balance back to the responsive 2/3-column layout.
                                            const columnBalanceKey = group.items
                                              .filter((item) => Boolean(item.rc.aiReason?.trim()))
                                              .map(({ originalIndex }) => {
                                                const reasonKey = `${m.personId}:${originalIndex}`;
                                                return `${originalIndex}:${expandedAiReasons.has(reasonKey) ? "open" : "closed"}`;
                                              })
                                              .join("|");
                                            return (
                                            <div key={group.kind} className="mb-4">
                                              {group.kind ? (
                                                <button
                                                  type="button"
                                                  aria-expanded={kindOpen}
                                                  onClick={() => {
                                                    holdCandidateAnchor(m.personId);
                                                    setCollapsedKinds((prev) => {
                                                      const next = new Set(prev);
                                                      if (next.has(kindKey)) next.delete(kindKey);
                                                      else next.add(kindKey);
                                                      return next;
                                                    });
                                                  }}
                                                  className="group flex w-full items-center gap-1.5 border-b border-ink/10 pb-1 pt-2 text-left outline-none transition focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/40"
                                                >
                                                  <span className="field-label font-semibold text-ink/55 transition group-hover:text-accent">
                                                    {group.kind}
                                                  </span>
                                                  <span className="text-[11px] tabular-nums text-ink/40">({group.items.length})</span>
                                                  <span
                                                    aria-hidden="true"
                                                    className={`shrink-0 text-[10px] text-ink/55 transition-transform duration-[var(--duration-base)] group-hover:text-accent ${
                                                      kindOpen ? "rotate-90" : ""
                                                    }`}
                                                  >
                                                    ›
                                                  </span>
                                                </button>
                                              ) : null}
                                              {kindOpen ? (
                                              <div
                                                key={columnBalanceKey}
                                                data-requirement-columns={`${m.personId}:${group.kind}`}
                                                className="gap-x-6 @3xl:columns-2 @7xl:columns-3"
                                              >
                                              {group.items.map(({ rc, originalIndex, met, unknown, lenient: lenientMatch }) => {
                                                const fulfillment = rc.fulfillment;
                                                const assessable = rc.assessable;
                                                const partial = fulfillment != null && fulfillment > 0 && fulfillment < 1;
                                                const label = labelForCondition(rc.condition, labelLookup, conditionLabelFormatters);
                                                const constraint = visibleConstraintSummary(constraintSummary(rc.condition));
                                                const actualText = formatActual(rc.condition, rc.actual);
                                                const pct = partial ? `${Math.round(fulfillment * 100)} %` : null;
                                                // Nicht bewertbar heißt genau eins: das Kriterium ist vorhanden, aber
                                                // nicht eingestuft. Nie "nicht vorhanden" (spark#175).
                                                const rightValue = !assessable
                                                  ? intl.formatMessage({
                                                      id: "match.result.ranking.notRated",
                                                      defaultMessage: "Vorhanden · Stufe nicht bewertet",
                                                    })
                                                  : actualText
                                                    ? pct
                                                      ? `${actualText} (${pct})`
                                                      : actualText
                                                    : pct;
                                                const aiReason = rc.aiReason?.trim() || null;
                                                const isMust = rc.requirement === "MUST";
                                                const requirementLabel = isMust
                                                  ? intl.formatMessage({ id: "match.result.ranking.must", defaultMessage: "Muss" })
                                                  : intl.formatMessage({ id: "match.result.ranking.optional", defaultMessage: "Kann" });
                                                const aiReasonKey = `${m.personId}:${originalIndex}`;
                                                const aiReasonOpen = aiReason ? expandedAiReasons.has(aiReasonKey) : false;
                                                return (
                                                  <div key={originalIndex} className="py-1.5 break-inside-avoid" data-testid={`candidate-requirement-${m.personId}-${unknown ? "unknown" : met ? "fulfilled" : "unfulfilled"}`}>
                                                    <div className="field-value grid min-w-0 grid-cols-[0.5rem_1.5rem_minmax(0,1fr)] items-start gap-2">
                                                      <span
                                                        aria-hidden="true"
                                                        title={
                                                          unknown
                                                            ? intl.formatMessage({
                                                                id: "match.result.ranking.dotUnknown",
                                                                defaultMessage: "Zu prüfen",
                                                              })
                                                            : met
                                                            ? lenientMatch
                                                              ? intl.formatMessage({
                                                                  id: "match.result.ranking.lenientMatchHint",
                                                                  defaultMessage:
                                                                    "Erfüllt per kulanter Anforderungsprüfung: Fachwissen vorhanden, geforderte Stufe nicht belegt.",
                                                                })
                                                              : intl.formatMessage({
                                                                  id: "match.result.ranking.dotFulfilled",
                                                                  defaultMessage: "Erfüllt",
                                                                })
                                                            : partial
                                                              ? intl.formatMessage({
                                                                  id: "match.result.ranking.dotPartial",
                                                                  defaultMessage: "Teilweise erfüllt",
                                                                })
                                                              : intl.formatMessage({
                                                                  id: "match.result.ranking.dotUnfulfilled",
                                                                  defaultMessage: "Nicht erfüllt",
                                                                })
                                                        }
                                                        className={`mt-[0.6rem] h-1.5 w-1.5 justify-self-center rounded-full ${
                                                          unknown
                                                            ? "bg-amber-500"
                                                            : met
                                                            ? lenientMatch
                                                              ? "bg-amber-500"
                                                              : "bg-emerald-500"
                                                            : partial
                                                              ? "bg-amber-500"
                                                              : "bg-rose-500"
                                                        }`}
                                                      />
                                                      <span
                                                        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition [&>svg]:h-3.5 [&>svg]:w-3.5 ${
                                                          isMust ? "" : "text-ink/40"
                                                        }`}
                                                        style={
                                                          isMust
                                                            ? { background: "var(--color-accent)", color: "var(--color-on-accent)" }
                                                            : undefined
                                                        }
                                                        title={requirementLabel}
                                                        aria-label={requirementLabel}
                                                      >
                                                        {isMust ? <LockClosedIcon /> : <LockOpenIcon />}
                                                      </span>
                                                      <div className="min-w-0 space-y-1.5">
                                                        <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5 text-ink/80">
                                                          <span className="min-w-0">{label}</span>
                                                          {constraint ? <span>({constraint})</span> : null}
                                                        </span>
                                                        {rightValue ? (
                                                          <div className="field-value text-[12px] leading-snug text-ink/55">
                                                            <span className="text-ink/40">
                                                              <FormattedMessage
                                                                id="match.result.ranking.actualInline"
                                                                defaultMessage="Nachweis:"
                                                              />{" "}
                                                            </span>
                                                            <span className="tabular-nums">{rightValue}</span>
                                                          </div>
                                                        ) : null}
                                                        {aiReason ? (
                                                          <div className="min-w-0">
                                                            <button
                                                              type="button"
                                                              aria-expanded={aiReasonOpen}
                                                              onClick={() => toggleAiReason(aiReasonKey)}
                                                              className="inline-flex shrink-0 items-center rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-[0.12em] text-accent ring-1 ring-accent/20 outline-none transition hover:bg-accent/10 focus-visible:ring-2 focus-visible:ring-ink/30"
                                                              title={intl.formatMessage({
                                                                id: "match.result.ranking.aiChipTitle",
                                                                defaultMessage: "Reason from the AI evaluation",
                                                              })}
                                                            >
                                                              <FormattedMessage id="match.result.ranking.aiChip" defaultMessage="AI Reason" />
                                                            </button>
                                                            {aiReasonOpen ? (
                                                              <div className="field-value mt-1 max-w-prose leading-snug text-ink/70">
                                                                {aiReason}
                                                              </div>
                                                            ) : null}
                                                          </div>
                                                        ) : null}
                                                      </div>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                              </div>
                                              ) : null}
                                            </div>
                                            );
                                          })
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                              </div>
                              ) : tab === "power" ? (
                                // Nur mounten, wenn diese Zeile wirklich offen ist: der umgebende
                                // Wrapper haelt den Inhalt aller Kandidaten dauerhaft im DOM (inert +
                                // CSS-Collapse statt Unmount, s.o.), sonst wuerde buildMemberDetail()
                                // fuer jeden Kandidaten laufen, der den Power-Fit-Tab je geoeffnet hatte.
                                open ? (
                                  <PowerFitDetail
                                    person={person ?? { id: m.personId, name: m.personName }}
                                    view={view}
                                    elementWeights={elementWeights}
                                  />
                                ) : null
                              ) : aiEnabled ? (
                                <CandidateAiAssessment
                                  assessment={
                                    view.factSkill?.teamMembers.find((tm) => tm.personId === m.personId)
                                      ?.aiAssessment
                                  }
                                  stacked
                                />
                              ) : (
                                <div className="max-w-prose py-2 text-[12px] text-ink/55">
                                  <FormattedMessage
                                    id="match.result.ranking.noAssessment"
                                    defaultMessage="Keine Smycles-Einschätzung vorhanden."
                                  />
                                </div>
                              )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              </Disclosure>
            </div>
          );
        })}
      </div>
    </div>
    </div>
  );
}

function visibleConstraintSummary(raw: string): string {
  return raw === "all" || raw === "any" || raw === "min 1" || raw === "exact 1" ? "" : raw;
}

function criterionKindLabel(condition: unknown, intl: IntlShape): string | null {
  if (!condition || typeof condition !== "object") return null;
  const obj = condition as { __typename?: unknown; name?: unknown };
  const typename = typeof obj.__typename === "string" ? obj.__typename : "";
  if (!typename && typeof obj.name === "string" && obj.name.startsWith("POWER:")) {
    return intl.formatMessage({ id: "match.result.ranking.kindPowers", defaultMessage: "Powers" });
  }
  switch (typename) {
    case "ExpertiseCondition":
      return intl.formatMessage({ id: "match.result.ranking.kindExpertise", defaultMessage: "Fachwissen" });
    case "EducationCondition":
      return intl.formatMessage({ id: "match.result.ranking.kindEducation", defaultMessage: "Ausbildung" });
    case "CertificateCondition":
      return intl.formatMessage({ id: "match.result.ranking.kindCertificate", defaultMessage: "Zertifikat" });
    case "ExperienceCondition":
    case "IndustryExperienceCondition":
      return intl.formatMessage({ id: "match.result.ranking.kindExperience", defaultMessage: "Berufserfahrung" });
    case "LanguageCondition":
      return intl.formatMessage({ id: "match.result.ranking.kindLanguage", defaultMessage: "Sprache" });
    case "AgeCondition":
    case "AnnualSalaryCondition":
    case "ChildrenCountCondition":
    case "CitizenshipCondition":
    case "CityCondition":
    case "CountryCondition":
    case "DriversLicenseCondition":
    case "GenderCondition":
    case "JobDepartmentCondition":
    case "JobLocationCondition":
    case "MaritalCondition":
    case "NationalityCondition":
      return intl.formatMessage({ id: "match.result.ranking.kindBasics", defaultMessage: "Personen-Details" });
    default:
      return null;
  }
}

// Reihenfolge der Kategorie-Gruppen analog zum Challenge-Editor
// (Personen-Details -> Berufserfahrung -> Ausbildung/Qualifikationen -> Fachwissen).
function criterionKindOrder(condition: unknown): number {
  if (!condition || typeof condition !== "object") return 90;
  const obj = condition as { __typename?: unknown; name?: unknown };
  const typename = typeof obj.__typename === "string" ? obj.__typename : "";
  if (!typename && typeof obj.name === "string" && obj.name.startsWith("POWER:")) return 6;
  switch (typename) {
    case "AgeCondition":
    case "AnnualSalaryCondition":
    case "ChildrenCountCondition":
    case "CitizenshipCondition":
    case "CityCondition":
    case "CountryCondition":
    case "DriversLicenseCondition":
    case "GenderCondition":
    case "JobDepartmentCondition":
    case "JobLocationCondition":
    case "MaritalCondition":
    case "NationalityCondition":
      return 0;
    case "ExperienceCondition":
    case "IndustryExperienceCondition":
      return 1;
    case "EducationCondition":
      return 2;
    case "CertificateCondition":
      return 3;
    case "LanguageCondition":
      return 4;
    case "ExpertiseCondition":
      return 5;
    default:
      return 90;
  }
}

// Kandidatenweite AI-Einschätzung ("menschliche Komponente" neben dem Score): Fließtext-
// Befunde in zwei Blöcken (Positiv/Negativ) + Fazit aus dem Prüfen-Lauf, einklappbar im
// selben Aufklapp-Muster wie die Kandidatenzeile (Chevron + grid-rows-Animation),
// zugeklappt als Default. Null/leer = nicht generiert (AI aus oder Call fehlgeschlagen),
// dann erscheint die Sektion nicht.
function CandidateAiAssessment({
  assessment,
  stacked,
}: {
  assessment: AiAssessmentView | null | undefined;
  stacked?: boolean;
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(true);
  if (!assessment) return null;
  const blocks = [
    {
      key: "positive",
      label: intl.formatMessage({ id: "match.result.ranking.assessment.positive", defaultMessage: "Positiv" }),
      text: assessment.positive,
    },
    {
      key: "negative",
      label: intl.formatMessage({ id: "match.result.ranking.assessment.negative", defaultMessage: "Negativ" }),
      text: assessment.negative,
    },
  ].filter((b) => b.text != null && b.text.trim().length > 0);
  const fazit = assessment.fazit != null && assessment.fazit.trim().length > 0 ? assessment.fazit : null;
  const interviewQuestions = (assessment.interviewQuestions ?? [])
    .map((question) => question.trim())
    .filter(Boolean)
    .slice(0, 3);
  if (blocks.length === 0 && !fazit && interviewQuestions.length === 0) return null;
  return (
    // Schmaler Container: unter den Kriterien mit Trennlinie oben. Breiter Container:
    // dritte Region rechts neben Anforderung/Nachweis, "Pipe"-Trenner links.
    <div
      className={
        stacked
          ? "mt-2 pb-1"
          : "mt-3 max-w-prose border-t border-ink/10 pt-2.5 @7xl:order-1 @7xl:mt-0 @7xl:min-w-0 @7xl:border-r @7xl:border-t-0 @7xl:pr-5 @7xl:pt-0"
      }
      data-testid="candidate-ai-assessment"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="group flex items-center gap-1.5 pb-1 outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
      >
        <span className="field-label font-semibold transition group-hover:text-accent">
          <FormattedMessage id="match.result.ranking.assessment.title" defaultMessage="Smycles Einschätzung" />
        </span>
        <span
          aria-hidden="true"
          className={`text-[11px] text-ink/40 transition-transform duration-[var(--duration-base)] ${
            open ? "rotate-90" : ""
          }`}
        >
          ›
        </span>
      </button>
      <div
        inert={!open}
        className={`grid transition-[grid-template-rows] duration-[var(--duration-base)] ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className={stacked ? "max-w-prose" : ""}>
            <SuitabilityBand value={assessment.suitability ?? null} />
          </div>
          {/* Flash sitzt nur auf dem Text-Block (ab Positiv), nicht auf Titel + Eignungs-Band. */}
          <div
            className={`pt-1 ${
              stacked ? "grid items-start gap-x-6 gap-y-4 @3xl:grid-cols-2 @7xl:grid-cols-4" : "space-y-2"
            }`}
          >
            {blocks.map((b) => {
              const text = b.text ?? "";
              const bulletStyle = isBulletAssessmentText(text);
              return (
                <div key={b.key} className="field-value leading-snug text-ink/70">
                  <span className={`font-medium text-ink ${bulletStyle ? "block" : ""}`}>
                    {b.label}:{bulletStyle ? null : " "}
                  </span>
                  {bulletStyle ? (
                    <AssessmentBulletList assessmentKey={b.key} text={text} />
                  ) : (
                    <span className="whitespace-pre-line">{text}</span>
                  )}
                </div>
              );
            })}
            {fazit ? (
              <div className={`field-value leading-snug text-ink ${stacked ? "" : "pt-1"}`}>
                <span className="font-medium">
                  <FormattedMessage id="match.result.ranking.assessment.fazit" defaultMessage="Fazit" />
                  {": "}
                </span>
                <span className="whitespace-pre-line">{fazit}</span>
              </div>
            ) : null}
            {interviewQuestions.length > 0 ? (
              <div className={stacked ? "" : "pt-2"} data-testid="candidate-interview-questions">
                <div className="field-label font-semibold text-ink/55">
                  <FormattedMessage
                    id="match.result.ranking.assessment.interviewQuestions"
                    defaultMessage="Fragen fürs Gespräch"
                  />
                </div>
                <ol className="mt-1.5 space-y-1.5">
                  {interviewQuestions.map((question, index) => (
                    <li key={`${index}:${question}`} className="field-value flex gap-2 leading-snug text-ink/70">
                      <span className="shrink-0 tabular-nums text-ink/40">{index + 1}.</span>
                      <span>{question}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function isBulletAssessmentText(text: string): boolean {
  return /^\s*(?:•|[-*])\s+/.test(text);
}

function assessmentBulletItems(text: string): string[] {
  const trimmed = text.trim();
  if (/^•/.test(trimmed)) {
    // The model occasionally returns inline or duplicated markers ("• • Text • Text").
    // In bullet mode every bullet glyph is a separator, so empty segments can be dropped safely.
    return trimmed.split(/\s*•\s*/).map((item) => item.trim()).filter(Boolean);
  }
  return trimmed
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*[-*]\s+/, "").trim())
    .filter(Boolean);
}

function AssessmentBulletList({
  assessmentKey,
  text,
}: {
  assessmentKey: string;
  text: string;
}) {
  return (
    <ul
      className="mt-1 space-y-2"
      data-testid={`candidate-ai-assessment-${assessmentKey}-bullets`}
    >
      {assessmentBulletItems(text).map((item, index) => (
        <li key={`${index}:${item}`} className="flex items-start gap-1.5">
          <span aria-hidden="true" className="shrink-0 text-ink/55">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

// Eignungs-Band: ungeeignet (links) .. sehr geeignet (rechts). Marker sitzt bei value% (0-100).
// value == null -> nur die Skala ohne Marker (genesis liefert suitability noch nicht).
// Qualitative Einordnung passend zur konservativen genesis-Kalibrierung (90+/75+/60+/40+/<40).
function suitabilityRating(pct: number) {
  if (pct >= 90) return <FormattedMessage id="match.result.ranking.assessment.ratingVeryHigh" defaultMessage="Sehr geeignet" />;
  if (pct >= 75) return <FormattedMessage id="match.result.ranking.assessment.ratingHigh" defaultMessage="Geeignet" />;
  if (pct >= 60) return <FormattedMessage id="match.result.ranking.assessment.ratingMedium" defaultMessage="Bedingt geeignet" />;
  if (pct >= 40) return <FormattedMessage id="match.result.ranking.assessment.ratingLow" defaultMessage="Eher ungeeignet" />;
  return <FormattedMessage id="match.result.ranking.assessment.ratingVeryLow" defaultMessage="Ungeeignet" />;
}

// Kompaktes Eignungs-Mass: qualitative Bewertung + Prozent prominent, darunter eine kurze Skala
// (max-w-xs statt volle Breite). Das eingeordnete Wort liest man sofort, die Bar ist nur Kontext.
function SuitabilityBand({ value }: { value: number | null | undefined }) {
  const pct = value == null ? null : Math.max(0, Math.min(100, value));
  return (
    <div className="mb-3 max-w-xs" data-testid="suitability-band">
      {pct != null ? (
        <div className="mb-1 flex items-baseline gap-1.5">
          <span className="field-value font-medium text-ink">{suitabilityRating(pct)}</span>
          <span className="text-[11px] tabular-nums text-ink/45">{pct} %</span>
        </div>
      ) : null}
      <div className="relative h-3">
        <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded bg-ink/10" />
        {pct == null ? null : (
          <>
            <div
              className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 rounded"
              style={{ width: `${pct}%`, background: "var(--color-accent)" }}
            />
            <div
              className="absolute left-0 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent ring-2 ring-page"
              style={{ left: `${pct}%` }}
            />
          </>
        )}
      </div>
    </div>
  );
}

function InsightTextList({
  items,
  empty,
}: {
  items: Array<{ key: string; label: string; sub?: string; value: number; right: string }>;
  empty: ReactNode;
}) {
  if (items.length === 0) {
    return <div className="mt-1 text-[12px] text-ink/55">{empty}</div>;
  }
  return (
    <div className="mt-2 space-y-2">
      {items.map((item) => (
        <div key={item.key} className="min-w-0 border-t border-ink/5 pt-2 first:border-t-0 first:pt-0">
          <div className="flex items-baseline justify-between gap-3">
            <div className="field-value min-w-0 truncate text-ink">{item.label}</div>
            <div className="shrink-0 text-[11px] tabular-nums text-ink/70">{item.right}</div>
          </div>
          <div className="mt-0.5 truncate text-[11px] tabular-nums text-ink/55">{item.sub}</div>
        </div>
      ))}
    </div>
  );
}

function InsightBarRow({
  item,
  max,
  formatActual,
  axisOnly = false,
}: {
  item: {
    key: string;
    label: string;
    sub?: string;
    value: number;
    right: string;
    detail?: string;
    distribution?: RankingActualDistribution;
    requiredValue?: number | null;
  };
  max: number;
  formatActual?: (condition: unknown, value: number | string) => string;
  // Verteilungs-Achse ersetzt den Anteils-Balken statt ihn zu doppeln (Bottlenecks:
  // die Luecke erzaehlt die Achse ueber den Abstand zum Soll-Marker).
  axisOnly?: boolean;
}) {
  // Achse nur mit Signal: sind alle erfassten Werte 0 (FULL_AI-Matches ohne harten
  // Nachweis, Stufe unbewertet), zeigt "Median 0 · Ø 0" Scheininformation und faellt weg.
  // Bottlenecks (axisOnly) fallen dann auf den Luecken-Balken zurueck.
  const numericDistribution =
    item.distribution?.kind === "numeric" && formatActual && item.distribution.max > 0
      ? item.distribution
      : null;
  const showBar = !(axisOnly && numericDistribution);
  return (
    <div className="min-w-0">
      <div className="field-value truncate text-ink">{item.label}</div>
      {showBar ? (
        <div className="mt-2">
          <div className="relative h-4">
            <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded bg-ink/10" />
            <div
              className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 rounded"
              style={{
                width: `${Math.max(3, Math.min(100, (item.value / max) * 100))}%`,
                background: "var(--color-accent)",
              }}
            />
          </div>
        </div>
      ) : null}
      <div className="mt-0.5 flex justify-between gap-3 text-[11px] tabular-nums text-ink/55">
        <span className="min-w-0 truncate">{item.sub}</span>
        <span className="shrink-0">{item.right}</span>
      </div>
      {numericDistribution && formatActual ? (
        <NumericDistributionAxis
          distribution={numericDistribution}
          formatActual={formatActual}
          requiredValue={item.requiredValue ?? null}
        />
      ) : null}
      {item.detail ? (
        <div className="mt-0.5 truncate text-[11px] tabular-nums text-ink/45" title={item.detail}>
          {item.detail}
        </div>
      ) : null}
    </div>
  );
}

// Werteverteilung als zweites Band unter dem Anteils-Balken: SD-Band, Median-Punkt und
// Ø-Strich nutzen dieselbe Glyphen-Sprache wie die fruehere Ist-Werte-Achse; die exakten
// Zahlen stehen in der detail-Zeile darunter.
function NumericDistributionAxis({
  distribution,
  formatActual,
  requiredValue = null,
}: {
  distribution: Extract<RankingActualDistribution, { kind: "numeric" }>;
  formatActual: (condition: unknown, value: number | string) => string;
  requiredValue?: number | null;
}) {
  const isExpertise =
    (distribution.condition as { __typename?: string } | null)?.__typename === "ExpertiseCondition";
  // Fachwissen: Achse fix ueber die volle Stufenskala (Storage 0..100 % = Stufe 0..7),
  // nicht Daten-Min/Max, damit die Marker-Lage zwischen Kriterien vergleichbar bleibt.
  // Der Soll-Wert weitet die Achse bei Bedarf, damit der Marker nie ausserhalb liegt.
  const axisMin = Math.min(isExpertise ? 0 : distribution.min, requiredValue ?? Infinity);
  const axisMax = Math.max(isExpertise ? 100 : distribution.max, requiredValue ?? -Infinity);
  const span = axisMax - axisMin;
  const position = (value: number) => {
    if (span <= 0) return 50;
    return Math.max(0, Math.min(100, ((value - axisMin) / span) * 100));
  };
  const sdStart = position(distribution.average - distribution.standardDeviation);
  const sdEnd = position(distribution.average + distribution.standardDeviation);
  return (
    <div className="mt-1">
      <div className="relative h-4">
        <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded bg-ink/10" />
        <div
          className="absolute top-1/2 h-3 -translate-y-1/2 rounded bg-accent/15"
          style={{ left: `${sdStart}%`, width: `${Math.max(2, sdEnd - sdStart)}%` }}
        />
        <div
          className="absolute -top-0.5 h-5 w-[2px] -translate-x-1/2 rounded bg-accent"
          style={{ left: `${position(distribution.average)}%` }}
        />
        <div
          className="absolute top-1 h-2 w-2 -translate-x-1/2 rounded-full bg-accent ring-2 ring-page"
          style={{ left: `${position(distribution.median)}%` }}
        />
        {requiredValue != null ? (
          <div
            className="absolute -top-1 -translate-x-1/2"
            style={{ left: `${position(requiredValue)}%` }}
          >
            <div className="h-0 w-0 border-x-[5px] border-x-transparent border-t-[6px] border-t-ink" />
          </div>
        ) : null}
      </div>
      <div className="mt-0.5 flex justify-between text-[10px] tabular-nums text-ink/45">
        <span>{formatActual(distribution.condition, axisMin)}</span>
        <span>{formatActual(distribution.condition, axisMax)}</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] tabular-nums text-ink/55">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-accent" aria-hidden />
          <FormattedMessage
            id="match.result.insights.medianShort"
            defaultMessage="Median {value}"
            values={{ value: formatActual(distribution.condition, distribution.median) }}
          />
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3.5 w-[2px] rounded bg-accent" aria-hidden />
          <FormattedMessage
            id="match.result.insights.averageShort"
            defaultMessage="Ø {value}"
            values={{ value: formatActual(distribution.condition, distribution.average) }}
          />
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-4 rounded bg-accent/15 ring-1 ring-accent/20" aria-hidden />
          <FormattedMessage
            id="match.result.insights.sdShort"
            defaultMessage="SD {value}"
            values={{ value: formatActual(distribution.condition, distribution.standardDeviation) }}
          />
        </span>
        {requiredValue != null ? (
          <span className="inline-flex items-center gap-1">
            <span
              className="inline-block h-0 w-0 border-x-[4px] border-x-transparent border-t-[5px] border-t-ink"
              aria-hidden
            />
            <FormattedMessage
              id="match.result.insights.requiredShort"
              defaultMessage="Soll {value}"
              values={{ value: formatActual(distribution.condition, requiredValue) }}
            />
          </span>
        ) : null}
      </div>
    </div>
  );
}
